import React from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetStaticPaths } from 'next/types';
import { AditionInitialization, AditionTagWithFallback } from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import { twMerge } from 'tailwind-merge';
import { RESSORT_ARTICLES_PER_LOAD } from 'tvm-config';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import * as PageElements from '@components/organisms/PageElements/PageElements';
import { cinemaSsrLogic } from '@components/pages/kino';
import { magazineSsrLogic } from '@components/pages/magazin';
import { ServicePage, serviceSsrLogic } from '@components/pages/service';
import { WatchlistPage, watchlistSsrLogic } from '@components/pages/watchlist';
import { DefaultOnlyLogo, DefaultNoSpacing, Article as ArticleLayout, Default } from '@components/templates/layouts';
import {
  GetAllArticlesPaginatedDocument,
  GetAllRessortsDocument,
  GetBySlugFullDocument,
  GetRessortByIdDocument,
} from '@src/lib/graphql/generated';
import { usePageTheme } from '@src/lib/hooks/usePageTheme';
import { decodeOptions } from '@src/utils/urlHelpers';
import { fetchExtraPageData } from '@lib/graphql/helpers/fetchExtraPageData';
import { createUrqlClient, ssrOptionalAuthQuery } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';

const SearchPage = dynamic(() => import('@components/pages/suche').then((mod) => mod['SearchPage']));
const RessortPage = dynamic(() =>
  import('@components/organisms/PageContents/RessortPage').then((mod) => mod['Ressort']),
);
const SubscriptionOverviewPage = dynamic(() =>
  import('@components/organisms/PageContents/SubscriptionOverviewPage').then((mod) => mod['SubscriptionOverviewPage']),
);
const CinemaGuide = dynamic(() => import('@components/pages/kino').then((mod) => mod['CinemaGuide']));
const MagazineIndex = dynamic(() => import('@components/pages/magazin').then((mod) => mod['MagazineIndex']));

// Empfehlungen
const ad_settings_Empfehlungen = {
  cad1: {
    id: 5256652,
    name: 'TVM_Empfehlungen_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_top: {
    id: 5256628,
    name: 'TVM_Mobile_Empfehlungen_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    //fixme: this is just the desktop ad duplicated for mobile
    id: 5256652,
    name: 'TVM_Mobile_Empfehlungen_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256628,
    name: 'TVM_Mobile_Empfehlungen_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

// Wissen
const ad_settings_Wissen = {
  cad1: {
    id: 5256659,
    name: 'TVM_Wissen_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_top: {
    id: 5256630,
    name: 'TVM_Mobile_Wissen_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256629,
    name: 'TVM_Mobile_Wissen_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256630,
    name: 'TVM_Mobile_Wissen_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

// Gewinnspiele
const ad_settings_Gewinnspiele = {
  cad1: {
    id: 5256664,
    name: 'TVM_Gewinnspiele_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_top: {
    id: 5256632,
    name: 'TVM_Mobile_Gewinnspiele_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256631,
    name: 'TVM_Mobile_Gewinnspiele_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256632,
    name: 'TVM_Mobile_Gewinnspiele_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

// Filme
const ad_settings_Filme = {
  billboard: {
    id: 5256674,
    name: 'TVM_Filme_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256622,
    name: 'TVM_Mobile_Filme_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256621,
    name: 'TVM_Mobile_Filme_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256622,
    name: 'TVM_Mobile_Filme_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

// Serien
const ad_settings_Serien = {
  billboard: {
    id: 5256671,
    name: 'TVM_Serien_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256626,
    name: 'TVM_Mobile_Serien_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256625,
    name: 'TVM_Mobile_Serien_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256626,
    name: 'TVM_Mobile_Serien_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

// Startseite
const ad_settings_Startseite = {
  billboard: {
    id: 5256676,
    name: 'TVM_Start_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  cad1: {
    id: 5256667,
    name: 'TVM_Start_MedRec_Cad_top',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  cad2: {
    id: 5256681,
    name: 'TVM_Start_MedRec_Cad_middle',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  cad3: {
    id: 5256682,
    name: 'TVM_Start_MedRec_Cad_bottom',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_top: {
    id: 5256607,
    name: 'TVM_Mobile_Start_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256606,
    name: 'TVM_Mobile_Start_MedRec_Cad_middle',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256607,
    name: 'TVM_Mobile_Start_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_bottom: {
    id: 5256604,
    name: 'TVM_Mobile_Start_MedRec_Cad_bottom',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function Page({ data, slug }) {
  usePageTheme(data?.power_branding);

  // set adsettings according to page url for pages without specific rendering logic
  if (slug === 'empfehlungen') {
    data.ad_settings = ad_settings_Empfehlungen;
  } else if (slug === 'wissen') {
    data.ad_settings = ad_settings_Wissen;
  } else if (slug === 'gewinnspiele') {
    data.ad_settings = ad_settings_Gewinnspiele;
  } else if (slug === 'filme') {
    data.ad_settings = ad_settings_Filme;
  } else if (slug === 'serien') {
    data.ad_settings = ad_settings_Serien;
  }

  if (slug === '/index') {
    return (
      <DefaultNoSpacing>
        <div>{data && <DefaultPage data={data} />}</div>
      </DefaultNoSpacing>
    );
  }

  if (slug === 'suche') {
    return (
      <Default>
        <SearchPage />
      </Default>
    );
  }

  if (slug === 'kino') {
    return (
      <Default>
        <CinemaGuide data={data} />
      </Default>
    );
  }

  if (slug === 'watchlist') {
    return (
      <DefaultNoSpacing>
        <WatchlistPage data={data} />
      </DefaultNoSpacing>
    );
  }

  if (slug === 'magazin') {
    return (
      <ArticleLayout>
        <MagazineIndex data={data} />
      </ArticleLayout>
    );
  }

  if (slug === 'service') {
    return <ServicePage data={data} />;
  }

  if (data.__typename === 'SubscriptionOverview') {
    return (
      <DefaultOnlyLogo>
        <SubscriptionOverviewPage data={data} />
      </DefaultOnlyLogo>
    );
  }

  if (data.isRessort) {
    return (
      <ArticleLayout>
        <RessortPage data={data} />
      </ArticleLayout>
    );
  }

  if (data.slug === 'home') data.ad_settings = ad_settings_Startseite;

  return (
    <DefaultNoSpacing>
      <DefaultPage data={data} />
    </DefaultNoSpacing>
  );
}

export const DefaultPage = ({ data }) => {
  const { title, show_title, pageContent, oewa, ad_settings } = data;

  const containerStyle = twMerge(
    'text-gray-600 dark',
    pageContent?.[0]?.__typename === 'PageContentElementHero' ||
      pageContent?.[0]?.__typename === 'PageContentElementHeroImages'
      ? ''
      : 'pt-10 mt-24',
  );

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Seite',
    titel: title,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'RedCont',
    oewaSubkontingent: oewa?.category_key || 'KulturUndFreizeit',
    oewaProfilingkontingent: oewa?.profile_key || 'FilmUndKino',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <>
      <Oewa
        categoryKey={oewa?.page_key || 'RedCont'}
        pageKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <AditionInitialization data={{ ad_settings }} />
      <Head>{Meta(data?.metadata)}</Head>
      <div className={containerStyle}>
        {show_title && title && (
          <Title level={1} classProps={{ heading: 'text-center mb-6 dark:text-white' }}>
            {title}
          </Title>
        )}
        {pageContent && <PageBody pageContent={pageContent} data={data} />}
      </div>
    </>
  );
};

export function PageBody({ pageContent, data }) {
  const { ad_settings } = data;
  let ad_count = 0;
  const router = useRouter();
  return (
    <div className="space-y-16 text-foreground md:space-y-32">
      {pageContent.map((element, idx) => {
        return (
          <div key={idx}>
            {idx > 1 &&
              (router.pathname == '/' ? idx === 2 || (idx % 3 === 0 && idx > 3) : idx % 3 === 0) &&
              ad_count < (idx > 9 ? 3 : 2) && (
                <div className="my-8 grid place-items-center lg:-mt-20 lg:mb-4">
                  <AditionTagWithFallback
                    tag={ad_settings.billboard}
                    tagCount={ad_count}
                    fallback={ad_count++ % 2 == 0 ? ad_settings.mobile_middle2 : ad_settings.mobile_middle1}
                    breakpoint={'lg'}
                  />
                </div>
              )}
            {element.headline &&
              element?.__typename !== 'PageContentElementHeroImages' &&
              element?.__typename !== 'PageContentElementEntityAd' && (
                <Title level={element.headline_level} classProps={{ heading: 'pb-4 dark:text-white' }}>
                  {element.headline}
                </Title>
              )}
            {!!PageElements?.[element.__typename] && PageElements?.[element.__typename]?.(element, idx, data)}
          </div>
        );
      })}

      <div className="mb-4 grid place-items-center lg:-mt-20">
        <AditionTagWithFallback
          tag={ad_settings.billboard}
          fallback={ad_settings.mobile_bottom}
          breakpoint={'lg'}
          tagCount={ad_count}
        />
      </div>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: ['/index', '/suche', '/kino', '/abo', '/magazin', '/service', '/watchlist'],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  const options = decodeOptions(params?.categoryOrPage);

  let result;

  if (options.slug === 'suche') {
    return {
      props: {
        slug: 'suche',
      },
    };
  }

  if (options.slug === 'kino') {
    const result = await cinemaSsrLogic();
    return result;
  }

  if (options.slug === 'magazin') {
    const result = await magazineSsrLogic();
    return result;
  }

  if (options.slug === 'service') {
    const result = await serviceSsrLogic();
    return result;
  }

  if (options.slug === 'watchlist') {
    const result = await watchlistSsrLogic();
    return result;
  }

  const slug = options?.slug === 'index' ? '/home' : options?.slug;

  const { data: findBySlugData } = await ssrOptionalAuthQuery({
    query: GetBySlugFullDocument,
    variables: {
      portal: 'tvmedia',
      slug: slug,
    },
    authToken: options?.params?.token,
  });

  const data = findBySlugData?.findBySlug;

  if (!data) return { notFound: true };

  const type = data?.__typename;

  if (type === 'Ressort') {
    const { data: ressortsData } = await ssrQuery({
      query: GetAllRessortsDocument,
      variables: { portal: 'tvmedia' },
    });
    const ressorts = ressortsData?.ressorts;

    const activeRessort = ressorts.find((e) => e.slug === options.slug);
    const isRessort = !!activeRessort;

    await ssrQuery({
      query: GetAllArticlesPaginatedDocument,
      variables: {
        portal: 'tvmedia',
        ressort: activeRessort?.id,
        first: RESSORT_ARTICLES_PER_LOAD,
        page: 1,
        orderBy: 'updated_at',
        orderDirection: 'DESC',
      },
    });

    const { data: ressortData } = await ssrQuery({
      query: GetRessortByIdDocument,
      variables: {
        portal: 'tvmedia',
        id: activeRessort.id,
      },
    });

    result = { ...data, isRessort, activeRessort, ressorts, oewa: ressortData.ressort.oewa };
  } else {
    result = {
      ...result,
      ...data,
      pageContent: data?.__typename === 'Page' ? await fetchExtraPageData(data?.pageContent) : null,
    };
  }

  function checkMetadataRedirect(): [number, string] {
    if (
      data?.__typename !== 'Redirect' &&
      data?.__typename !== 'Article' &&
      data?.__typename !== 'SubscriptionOverview' &&
      data?.metadata?.redirect
    ) {
      return [null, data?.metadata?.redirect];
    }
    return null;
  }

  const redirect: [number, string] =
    data?.__typename === 'Redirect' && data?.redirect_type && data?.redirect_target
      ? [data?.redirect_type, data?.redirect_target]
      : checkMetadataRedirect();

  // Handle redirect result
  if (redirect) {
    return handleRedirect(...redirect);
  }

  const props = {
    data: result,
    slug: options?.slug || '/home',
    urqlState: null,
  };

  if (type === 'Ressort') {
    props.urqlState = ssrCache.extractData();
  }

  return {
    props,
  };
};

export default Page;
